import cn from 'classnames';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  DocumentSectionCitation,
  LiteratureReference,
  TranscriptChunk,
} from '@/shared/api/transcriptor/transcriptorApi';
import { Textarea } from '@/shared/ui';

import { ConsultationNode } from '../../config';
import { CitationPopup } from '../CitationPopup/CitationPopup';
import { ConsultationContent } from '../ConsultationContent/ConsultationContent';
import { ScoreBar } from '../ScoreBar/ScoreBar';

import styles from './ConsultationSection.module.scss';

type ConsultationSectionProps = {
  section: ConsultationNode['children'][0];
  nodeIndex: number;
  sectionIndex: number;
  isEditing: boolean;
  transcriptChunks?: TranscriptChunk[];
  literatureReferences?: LiteratureReference[] | null;
  handleTextChange: (
    nodeIndex: number,
    sectionIndex: number,
    contentIndex: number,
    value: string,
  ) => void;
};

export const ConsultationSection: FC<ConsultationSectionProps> = ({
  section,
  nodeIndex,
  sectionIndex,
  isEditing,
  transcriptChunks,
  literatureReferences,
  handleTextChange,
}) => {
  const { formatMessage } = useIntl();

  const [citationPopupState, setCitationPopupState] = useState<{
    isOpen: boolean;
    citations: DocumentSectionCitation[];
    title: string;
  }>({
    isOpen: false,
    citations: [],
    title: '',
  });

  // Check if section has any non-empty content
  const hasContent = section.content.some(
    (contentItem) => contentItem.text.trim() !== '',
  );

  // Show section if it has content OR if we're in edit mode
  if (!hasContent && !isEditing) {
    return null;
  }

  const handleCitationClick = (
    citations: DocumentSectionCitation[],
    title: string,
  ) => {
    setCitationPopupState({
      isOpen: true,
      citations,
      title,
    });
  };

  const handleCitationClose = () => {
    setCitationPopupState({
      isOpen: false,
      citations: [],
      title: '',
    });
  };

  const sectionTitle = section.titleKey
    ? formatMessage({
        id: section.titleKey,
        defaultMessage: section.title,
      })
    : section.title;

  return (
    <>
      <div className={styles.dataListItem}>
        <div className={styles.dataListLabelContainer}>
          <div className={cn('p2', styles.dataListLabel)}>{sectionTitle}</div>
          {section.score !== undefined && (
            <div className={styles.scoreWrapper}>
              <ScoreBar score={section.score} />
            </div>
          )}
        </div>
        <div className={styles.dataListValue}>
          <div className={styles.valueContent}>
            {section.content.length > 0
              ? section.content.map((content, contentIndex) => {
                  const hasCitations =
                    !isEditing &&
                    content.citations &&
                    content.citations.length > 0 &&
                    transcriptChunks &&
                    transcriptChunks.length > 0;

                  return (
                    <div key={contentIndex} className={styles.textBlock}>
                      {isEditing ? (
                        <Textarea
                          className={cn('p2')}
                          value={content.text}
                          onChange={(e) => {
                            handleTextChange(
                              nodeIndex,
                              sectionIndex,
                              contentIndex,
                              e.target.value,
                            );
                          }}
                          isElastic
                          isResizeDisabled
                        />
                      ) : (
                        <div
                          className={cn(hasCitations && styles.citationLink)}
                          role={hasCitations ? 'button' : undefined}
                          tabIndex={hasCitations ? 0 : undefined}
                          onClick={
                            hasCitations
                              ? () =>
                                  handleCitationClick(
                                    content.citations!,
                                    sectionTitle,
                                  )
                              : undefined
                          }
                          onKeyDown={
                            hasCitations
                              ? (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleCitationClick(
                                      content.citations!,
                                      sectionTitle,
                                    );
                                  }
                                }
                              : undefined
                          }
                        >
                          <ConsultationContent
                            text={content.text}
                            literatureReferences={literatureReferences}
                          />
                        </div>
                      )}
                    </div>
                  );
                })
              : // Show empty textarea in edit mode for sections without content
                isEditing && (
                  <div className={styles.textBlock}>
                    <Textarea
                      className={cn('p2')}
                      value=""
                      onChange={(e) => {
                        handleTextChange(
                          nodeIndex,
                          sectionIndex,
                          0,
                          e.target.value,
                        );
                      }}
                      isElastic
                      isResizeDisabled
                    />
                  </div>
                )}
          </div>
        </div>
      </div>

      {transcriptChunks && transcriptChunks.length > 0 && (
        <CitationPopup
          isOpen={citationPopupState.isOpen}
          title={citationPopupState.title}
          citations={citationPopupState.citations}
          transcriptChunks={transcriptChunks}
          onClose={handleCitationClose}
        />
      )}
    </>
  );
};
